import { Component, DestroyRef, ViewChild, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent } from '@aprova-digital/design-system';

import { City } from '../../../domain/city/entities/city.entity';
import { Types } from '../../../domain/types.domain';
import { CreateAccountOuput } from '../../../domain/user/dtos/create-account.output';
import {
  StepperComponent,
  StepConfig,
  StepperEvent,
  LoadingBarComponent,
  IconComponent,
} from '../../../@ads/components';

import { AppHandler } from '../../app.handler';
import { AuthRepositoryInstruction } from '../../../builder/instructions/repositories/auth.repository.instructions';
import { UserRepositoryInstruction } from '../../../builder/instructions/repositories/user.repository.instructions';
import { CreateAccountServiceInstruction } from '../../../builder/instructions/services/create-account.service.instruction';
import { RepresentativeRepositoryInstruction } from '../../../builder/instructions/repositories/representative.repository.instruction';
import { ValidateRfServiceInstruction } from '../../../builder/instructions/services/validate-rf.service.instruction';
import { UploadDocumentServiceInstruction } from '../../../builder/instructions/services/upload-document.service.instruction';

import { CreateAccountPjFormHandler } from './create-account-pj-form.handler';
import { CompanyFormComponent } from './components/company-form/company-form.component';
import { AddressFormComponent } from '../create-account/components/address-form/address-form.component';
import { RepresentativeFormComponent } from './components/representative-form/representative-form.component';
import { VerifiedRepresentative } from '../../../domain/auth/dtos/start-account-creation.output';
import { RfValidationComponent } from './components/rf-validation/rf-validation.component';
import { PjPasswordFormComponent } from './components/pj-password-form/pj-password-form.component';
import { PageTitleComponent } from '../../components/abstracts/page-title.component';
import { AccountFormHandler } from '../create-account/account-form.handler';

/** Vinda da lista do que criar depois do gov.br: quem autenticou já entra como representante. */
export interface CreateAccountPjState {
  verifiedRepresentative?: VerifiedRepresentative;
}

@Component({
  selector: 'app-create-account-pj',
  standalone: true,
  imports: [
    ButtonComponent,
    StepperComponent,
    LoadingBarComponent,
    IconComponent,
    CompanyFormComponent,
    AddressFormComponent,
    RepresentativeFormComponent,
    RfValidationComponent,
    PjPasswordFormComponent,
  ],
  providers: [
    CreateAccountPjFormHandler,
    AccountFormHandler,
    new AuthRepositoryInstruction(),
    new UserRepositoryInstruction(),
    new CreateAccountServiceInstruction(),
    new RepresentativeRepositoryInstruction(),
    new ValidateRfServiceInstruction(),
    new UploadDocumentServiceInstruction(),
  ],
  templateUrl: './create-account-pj.component.html',
  styleUrl: './create-account-pj.component.scss',
})
export class CreateAccountPjComponent extends PageTitleComponent(
  'Criar conta PJ'
) {
  @ViewChild(CompanyFormComponent)
  private _companyFormRef: CompanyFormComponent | undefined;

  @ViewChild(RfValidationComponent)
  private _rfValidationRef: RfValidationComponent | undefined;

  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _appHandler = inject(AppHandler);
  private readonly _handler = inject(CreateAccountPjFormHandler);
  private readonly _accountFormHandler = inject(AccountFormHandler);
  private readonly _authRepository = inject(Types.AuthRepository);
  private readonly _createAccountService = inject(Types.CreateAccountService);
  private readonly _representativeRepository = inject(Types.RepresentativeRepository);
  private readonly _verifiedRepresentative = (
    this._router.getCurrentNavigation()?.extras?.state as CreateAccountPjState | undefined
  )?.verifiedRepresentative;

  public cityEntity: City | undefined;
  public cityIdentity: string = '';
  public cityConnection: string = '';
  public hideRecaptcha = false;
  public recaptchaReactive = '';
  public cityLoginUrl: string = '';

  public saving = signal(false);
  public stepping = signal(false);
  public accountCreated = signal(false);
  public inRfIntroState = signal(false);

  public steps: StepConfig[] = [
    { id: 'dados', title: 'Dados', enabled: true },
    { id: 'endereco', title: 'Endereço', enabled: true },
    { id: 'representante', title: 'Representante', enabled: true },
    { id: 'validacao', title: 'Validação', enabled: true },
    { id: 'senha', title: 'Senha', enabled: true },
  ];

  private readonly _stepValidity = signal<boolean[]>(
    new Array(this.steps.length).fill(false)
  );

  get currentStep(): number {
    return this._handler.getStep();
  }

  get isCurrentStepValid(): boolean {
    return this._stepValidity()[this.currentStep] ?? false;
  }

  ngOnInit(): void {
    this.setupHistoryRestoreGuard();

    const verifiedRepresentative = this._verifiedRepresentative;
    if (verifiedRepresentative) {
      this._handler.setVerifiedRepresentative({
        cpf: verifiedRepresentative.cpf,
        nome: verifiedRepresentative.name,
        email: verifiedRepresentative.email,
      });
    }

    this._appHandler.setBackButtonHome(false);
    this._appHandler.hideBackButton(true);

    this._route.data.subscribe(async ({ city }) => {
      if (!city?.active) {
        this._appHandler.setLoading(false);
        this._appHandler.setError(true, {
          message: 'Indisponível',
          description: 'Consulte a organização para maiores esclarecimentos',
        });
        return;
      }

      const config = await this._authRepository.getConfigByCityId(city.index);

      if (!config?.allowUseCnpj) {
        this._router.navigate([`/${city.identidade}/create-account`]);
        return;
      }

      this.hideRecaptcha = config?.hideRecaptcha ?? false;

      this.cityEntity = city;
      this.cityIdentity = city.identidade;
      this.cityConnection = city.connection;
      this.cityLoginUrl = city.loginUrl;
      this._appHandler.setLoading(false);
    });
  }

  onChangeRecaptcha(token: string): void {
    this.recaptchaReactive = token;

  }

  redirectToLogin(): void {
    if (this.cityLoginUrl) {
      window.location.href = this.cityLoginUrl;
      return;
    }

    this._router.navigate([`/${this.cityIdentity}`]);
  }

  setStepValidation(stepNumber: number, valid: boolean): void {
    this._stepValidity.update((v) => {
      const next = [...v];
      next[stepNumber] = valid;
      return next;
    });
  }

  onStepChange(event: StepperEvent): void {
    this._handler.setStep(event.currentStep);
  }

  goBack(): void {
    if (this.currentStep === 0) {
      this._router.navigate([`/${this.cityIdentity}/select-account-type`]);
      return;
    }
    this._handler.backStep();
    this.setStepValidation(this.currentStep, false);
  }

  onRfBackToReview(): void {
    this.inRfIntroState.set(false);
    this.goBack();
  }

  async goToNextStep(): Promise<void> {
    if (this.stepping()) return;

    const next = this.currentStep + 1;

    // Step 0 (dados da empresa) — validar CNPJ e e-mail contra o backend
    if (this.currentStep === 0 && this._companyFormRef) {
      this.stepping.set(true);
      try {
        const isValid = await this._companyFormRef.validateEmailAndCnpj();
        if (!isValid) return;
      } finally {
        this.stepping.set(false);
      }
    }

    // Step 3 (validação RF) — trigger upload if document required before advancing
    if (this.currentStep === 3 && this._rfValidationRef?.requiresDocument) {
      this.stepping.set(true);
      try {
        const uploaded = await this._rfValidationRef.uploadDocumentForAll();
        if (!uploaded) return;
      } finally {
        this.stepping.set(false);
      }
    }

    if (next > this.steps.length - 1) {
      this.createCompanyAccount();
      return;
    }

    this._handler.setStep(next);
  }

  createCompanyAccount(): void {
    this.saving.set(true);

    const company = this._handler.getCompanyInfo()!;
    const address = this._accountFormHandler.getAddressInfo() ?? {};
    const { noNumberAddress: _noNum, ...addressForApi } = address;
    const password = this._handler.getPassword();
    const representatives = this._handler.getRepresentatives().map((r) => {
      const rfResult = this._handler.getRfResults().get(r.cpf);
      return {
        ...r,
        validationMethod: (rfResult?.documentUrl ? 'document' : 'receita_federal') as 'document' | 'receita_federal',
        documentUrl: rfResult?.documentUrl,
        termAcceptedAt: rfResult?.termAcceptedAt,
      };
    });

    this._createAccountService
      .handle({
        type: 'business',
        connection: this.cityConnection,
        nome: company.razaoSocial,
        sobrenome: company.nomeFantasia,
        email: company.email,
        senha: password,
        cpf: company.cnpj,
        telefone: company.telefone,
        cep: addressForApi.cep ?? '',
        rua: addressForApi.rua ?? '',
        estado: addressForApi.estado ?? '',
        cidade: addressForApi.cidade ?? '',
        bairro: addressForApi.bairro ?? '',
        numero: addressForApi.numero ?? '',
        complemento: addressForApi.complemento,
        verifyMail: true,
        recaptchaReactive: this.recaptchaReactive,
        termsAccepted: this._handler.getTermsAccepted(),
        cityControl: {
          city: this.cityIdentity.slice(0, this.cityIdentity.length - 2),
          state: this.cityIdentity.slice(this.cityIdentity.length - 2),
        },
      })
      .then(async (user: CreateAccountOuput) => {
        const companyUserId = user.userId || user.user_id;
        const registrationToken = user.representativeRegistrationToken;
        if (companyUserId && !registrationToken) {
          console.error('[APR-3531] Conta PJ criada sem representativeRegistrationToken; representantes ficam para o pós-login');
        }
        if (companyUserId && registrationToken) {
          // Retry com delay para aguardar sync Auth0 → MongoDB
          for (let attempt = 1; attempt <= 3; attempt++) {
            try {
              await this._representativeRepository.registerRepresentatives({
                companyUserId,
                cityId: this.cityEntity?.index ?? 0,
                representatives,
                registrationToken,
              });
              break;
            } catch (err) {
              console.error(`[APR-278] Tentativa ${attempt}/3 falhou:`, err);
              if (attempt < 3) {
                await new Promise((r) => setTimeout(r, attempt * 2000));
              }
              // Fallback apos 3 tentativas: representantes serao adicionados pos-login
            }
          }
        }
        this.saving.set(false);
        this.accountCreated.set(true);
      })
      .catch((err) => {
        this.saving.set(false);
        const description = err.error?.message ?? err.message;
        this._appHandler.setError(true, {
          message: 'Não foi possível criar a conta',
          description,
          actionText: 'Tentar novamente',
          action: () => {
            this._appHandler.clearError();
          },
        });
      });
  }

  private setupHistoryRestoreGuard(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const handlePageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) {
        return;
      }

      this.resetSuccessState();
    };

    window.addEventListener('pageshow', handlePageShow);
    this._destroyRef.onDestroy(() => {
      window.removeEventListener('pageshow', handlePageShow);
    });
  }

  private resetSuccessState(): void {
    this.saving.set(false);
    this.accountCreated.set(false);
  }

}
